import { Button, Group, Modal, Stack } from '@mantine/core';
import { IconArrowBackUp, IconArrowForwardUp } from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { clientPointToImagePixel } from '@/lib/bugReportAnnotate';
import { canvasToJpegBase64UnderCap } from '@/lib/captureViewportScreenshot';

export interface BugReportAnnotateModalProps {
  opened: boolean;
  screenshotDataUrl: string;
  onCancel: () => void;
  onDone: (jpegBase64: string) => void;
}

export function BugReportAnnotateModal({
  opened,
  screenshotDataUrl,
  onCancel,
  onDone,
}: BugReportAnnotateModalProps): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const drawing = useRef(false);
  const [ready, setReady] = useState(false);

  // Undo/redo history: array of ImageData snapshots, cursor points at current state
  const history = useRef<ImageData[]>([]);
  const historyCursor = useRef(-1);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const syncUndoRedo = (): void => {
    setCanUndo(historyCursor.current > 0);
    setCanRedo(historyCursor.current < history.current.length - 1);
  };

  const pushSnapshot = (): void => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    // Discard any redo states ahead of the cursor
    history.current = history.current.slice(0, historyCursor.current + 1);
    history.current.push(ctx.getImageData(0, 0, canvas.width, canvas.height));
    historyCursor.current = history.current.length - 1;
    syncUndoRedo();
  };

  const restoreSnapshot = (idx: number): void => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    const snapshot = history.current[idx];
    if (!canvas || !ctx || !snapshot) return;
    ctx.putImageData(snapshot, 0, 0);
    historyCursor.current = idx;
    syncUndoRedo();
  };

  const paintImage = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img) return;
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(img, 0, 0);
    // Seed history with the clean image
    history.current = [ctx.getImageData(0, 0, canvas.width, canvas.height)];
    historyCursor.current = 0;
    setCanUndo(false);
    setCanRedo(false);
    setReady(true);
  }, []);

  useEffect(() => {
    if (!opened) {
      setReady(false);
      return;
    }
    const img = new Image();
    img.onload = () => {
      imageRef.current = img;
      paintImage();
    };
    img.src = screenshotDataUrl;
  }, [opened, screenshotDataUrl, paintImage]);

  const pointFromEvent = (clientX: number, clientY: number): { x: number; y: number } | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    return clientPointToImagePixel(clientX, clientY, rect, canvas.width, canvas.height);
  };

  const strokeColor = (): string =>
    getComputedStyle(document.documentElement).getPropertyValue('--mantine-color-red-6').trim()
    || 'currentColor';

  const onPointerDown = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    const canvas = canvasRef.current;
    const pt = pointFromEvent(event.clientX, event.clientY);
    const ctx = canvas?.getContext('2d');
    if (!canvas || !pt || !ctx) return;
    drawing.current = true;
    canvas.setPointerCapture(event.pointerId);
    ctx.strokeStyle = strokeColor();
    ctx.lineWidth = Math.max(2, canvas.width / 400);
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(pt.x, pt.y);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    if (!drawing.current) return;
    const pt = pointFromEvent(event.clientX, event.clientY);
    const ctx = canvasRef.current?.getContext('2d');
    if (!pt || !ctx) return;
    ctx.lineTo(pt.x, pt.y);
    ctx.stroke();
  };

  const onPointerUp = (): void => {
    if (drawing.current) {
      drawing.current = false;
      pushSnapshot();
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onCancel}
      title="Highlight the issue"
      fullScreen
      zIndex={400}
      trapFocus
      styles={{
        content: { display: 'flex', flexDirection: 'column' },
        body: { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 },
      }}
    >
      <Stack gap="md" h="100%">
        <div
          ref={wrapRef}
          style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <canvas
            ref={canvasRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
            aria-label="Draw on screenshot"
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              display: ready ? 'block' : 'none',
              border: '1px solid var(--mantine-color-gray-4)',
              touchAction: 'none',
              cursor: 'crosshair',
            }}
          />
        </div>
        <Group justify="space-between">
          <Group gap="xs">
            <Button
              variant="default"
              leftSection={<IconArrowBackUp size={16} />}
              disabled={!canUndo}
              onClick={() => restoreSnapshot(historyCursor.current - 1)}
            >
              Undo
            </Button>
            <Button
              variant="default"
              leftSection={<IconArrowForwardUp size={16} />}
              disabled={!canRedo}
              onClick={() => restoreSnapshot(historyCursor.current + 1)}
            >
              Redo
            </Button>
          </Group>
          <Group>
            <Button variant="default" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                const canvas = canvasRef.current;
                if (!canvas) return;
                onDone(canvasToJpegBase64UnderCap(canvas));
              }}
              disabled={!ready}
            >
              Done
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
